const DEFAULT_SLOW_MS = 6000;

const shouldEnableRequestTiming = () => {
  const configured = process.env.REACT_APP_REQUEST_TIMING_ENABLED;

  if (configured != null) {
    return configured === 'true';
  }

  return process.env.REACT_APP_ENV === 'development';
};

const getSlowThresholdMs = () => {
  const parsed = Number.parseInt(process.env.REACT_APP_REQUEST_TIMING_SLOW_MS, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : DEFAULT_SLOW_MS;
};

const getUrlLabel = resource => {
  if (typeof resource === 'string') {
    return resource;
  }

  if (resource?.url) {
    return resource.url;
  }

  return String(resource);
};

const getMethodLabel = (resource, options) => {
  if (options?.method) {
    return options.method;
  }

  if (resource?.method) {
    return resource.method;
  }

  return 'GET';
};

const installFetchTiming = slowThresholdMs => {
  if (typeof window.fetch !== 'function' || window.fetch.__riticketTimed) {
    return;
  }

  const originalFetch = window.fetch.bind(window);

  const timedFetch = async (resource, options) => {
    const start = window.performance.now();

    try {
      const response = await originalFetch(resource, options);
      const durationMs = window.performance.now() - start;

      if (durationMs >= slowThresholdMs) {
        console.warn(
          `[slow-fetch] ${getMethodLabel(resource, options)} ${getUrlLabel(resource)} ${
            response.status
          } ${Math.round(durationMs)}ms`
        );
      }

      return response;
    } catch (error) {
      const durationMs = window.performance.now() - start;

      if (durationMs >= slowThresholdMs) {
        console.warn(
          `[slow-fetch] ${getMethodLabel(resource, options)} ${getUrlLabel(
            resource
          )} failed ${Math.round(durationMs)}ms`
        );
      }

      throw error;
    }
  };

  timedFetch.__riticketTimed = true;
  timedFetch.__riticketOriginalFetch = originalFetch;
  window.fetch = timedFetch;
};

const installXhrTiming = slowThresholdMs => {
  if (typeof window.XMLHttpRequest !== 'function' || window.XMLHttpRequest.__riticketTimed) {
    return;
  }

  const OriginalXMLHttpRequest = window.XMLHttpRequest;

  function TimedXMLHttpRequest() {
    const xhr = new OriginalXMLHttpRequest();
    let method = 'GET';
    let url = '';
    let start = 0;

    const originalOpen = xhr.open;
    xhr.open = function open(nextMethod, nextUrl, ...args) {
      method = nextMethod || method;
      url = nextUrl || url;
      return originalOpen.call(xhr, nextMethod, nextUrl, ...args);
    };

    const originalSend = xhr.send;
    xhr.send = function send(...args) {
      start = window.performance.now();

      xhr.addEventListener('loadend', () => {
        const durationMs = window.performance.now() - start;

        if (durationMs >= slowThresholdMs) {
          console.warn(`[slow-xhr] ${method} ${url} ${xhr.status} ${Math.round(durationMs)}ms`);
        }
      });

      return originalSend.apply(xhr, args);
    };

    return xhr;
  }

  TimedXMLHttpRequest.prototype = OriginalXMLHttpRequest.prototype;
  Object.getOwnPropertyNames(OriginalXMLHttpRequest).forEach(key => {
    if (key === 'prototype') {
      return;
    }

    Object.defineProperty(
      TimedXMLHttpRequest,
      key,
      Object.getOwnPropertyDescriptor(OriginalXMLHttpRequest, key)
    );
  });
  TimedXMLHttpRequest.__riticketTimed = true;
  TimedXMLHttpRequest.__riticketOriginalXMLHttpRequest = OriginalXMLHttpRequest;
  window.XMLHttpRequest = TimedXMLHttpRequest;
};

export const installRequestTiming = () => {
  if (typeof window === 'undefined' || !window.performance || !shouldEnableRequestTiming()) {
    return;
  }

  const slowThresholdMs = getSlowThresholdMs();

  installFetchTiming(slowThresholdMs);
  installXhrTiming(slowThresholdMs);
};
